import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Save,
  Trash2,
  ArrowLeft,
  Paperclip,
  X,
  Upload,
  UserCircle2,
  Phone,
  Mail,
  MapPin,
  Tag,
  CreditCard,
  Building2,
  Printer,
  CheckCircle2,
  Clock,
  ShieldCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { SEGMENTS } from "../data/catalog";

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const idx = String(result).indexOf(",");
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const formatBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const PrintRow = ({ label, value, mono, bold, fullSpan }) => (
  <div className={fullSpan ? "col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-wider text-[#5b6b4a]">
      {label}
    </div>
    <div
      className={`mt-0.5 ${mono ? "font-mono" : ""} ${
        bold ? "font-semibold text-[#1d3a14]" : "text-[#1f2a1a]"
      }`}
    >
      {value}
    </div>
  </div>
);

const downloadBase64 = (name, type, b64) => {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], {
      type: type || "application/octet-stream",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download file");
  }
};

export default function CustomerForm() {
  const { id } = useParams(); // undefined when creating
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [salespersons, setSalespersons] = useState([]);
  const [createdAt, setCreatedAt] = useState("");
  const [status, setStatus] = useState("pending");
  const [approvedBy, setApprovedBy] = useState("");
  const [approvedAt, setApprovedAt] = useState("");

  const [form, setForm] = useState({
    company_name: "",
    mobile: "",
    email: "",
    address: "",
    city: "",
    state: "",
    gstin: "",
    payment_terms: "",
    segment: "",
    assigned_salesperson_email: user?.role === "salesperson" ? user.email : "",
    attachments: [],
  });

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const canEditAssignedSP = user?.role !== "salesperson";
  const canDelete =
    user?.role === "coordinator" || user?.is_admin === true;

  useEffect(() => {
    api
      .get("/salespersons")
      .then(({ data }) => setSalespersons(data || []))
      .catch(() => {
        /* ignore — salespersons might not have access */
      });
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/customers/${id}`);
        setForm({
          company_name: data.company_name || "",
          mobile: data.mobile || "",
          email: data.email || "",
          address: data.address || "",
          city: data.city || "",
          state: data.state || "",
          gstin: data.gstin || "",
          payment_terms: data.payment_terms || "",
          segment: data.segment || "",
          assigned_salesperson_email: data.assigned_salesperson_email || "",
          attachments: data.attachments || [],
        });
        setCreatedAt(data.created_at);
        setStatus(data.status || "approved");
        setApprovedBy(data.approved_by || "");
        setApprovedAt(data.approved_at || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load customer");
        navigate("/customers");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const items = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 5MB — skipped`);
        continue;
      }
      const data_base64 = await readFileAsBase64(f);
      items.push({
        name: f.name,
        size: f.size,
        type: f.type || "file",
        data_base64,
      });
    }
    if (items.length) {
      setForm((p) => ({ ...p, attachments: [...p.attachments, ...items] }));
      toast.success(`${items.length} file(s) attached`);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx) =>
    setForm((p) => ({
      ...p,
      attachments: p.attachments.filter((_, i) => i !== idx),
    }));

  const handleSubmit = async () => {
    if (!form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!form.mobile.trim()) {
      toast.error("Mobile number is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        email: form.email || undefined,
        assigned_salesperson_email: form.assigned_salesperson_email || undefined,
      };
      if (isEdit) {
        await api.patch(`/customers/${id}`, payload);
        toast.success("Customer updated");
        navigate("/customers");
      } else {
        const { data } = await api.post("/customers", payload);
        toast.success(`${data.company_name} added`);
        navigate("/customers");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Permanently delete ${form.company_name}?\n\nThis cannot be undone.`
      )
    )
      return;
    setSaving(true);
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      navigate("/customers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setSaving(false);
    }
  };

  const handlePrint = () => window.print();

  const handleApprove = async () => {
    if (!window.confirm(`Approve ${form.company_name}?`)) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/customers/${id}/approve`);
      toast.success(`${data.company_name} approved`);
      setStatus(data.status);
      setApprovedBy(data.approved_by);
      setApprovedAt(data.approved_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setSaving(false);
    }
  };

  const isApproved = isEdit && status === "approved";
  const isPending = isEdit && status === "pending";
  const canApprove =
    isPending && (user?.role === "coordinator" || user?.is_admin === true);
  const isLockedForUser =
    isApproved && user?.role === "salesperson"; // matches backend rule

  if (loading) {
    return (
      <div
        className="text-center py-16 text-[#5b6b4a]"
        data-testid="customer-form-loading"
      >
        Loading…
      </div>
    );
  }

  return (
    <div className="grain ui-sans pb-16" data-testid="customer-form-page">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 mt-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3 no-print">
          <Button
            variant="outline"
            onClick={() => navigate("/customers")}
            data-testid="back-to-customers"
            className="border-[#c4b991] hover:bg-[#efe7cf]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>
          <h1 className="brand-serif text-3xl font-bold text-[#1d3a14]">
            {isEdit ? "Edit Customer" : "New Customer"}
          </h1>
          {isEdit && (
            <Button
              variant="outline"
              onClick={handlePrint}
              data-testid="print-customer-button"
              className="border-[#c4b991] hover:bg-[#efe7cf]"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          )}
        </div>

        {isEdit && (
          <div
            className={cn(
              "no-print flex items-center justify-between flex-wrap gap-3 rounded-xl border px-4 py-3",
              isApproved
                ? "bg-[#1d3a14]/10 border-[#1d3a14]/30"
                : "bg-[#c47b28]/10 border-[#c47b28]/40"
            )}
            data-testid="customer-status-banner"
          >
            <div className="flex items-center gap-2 text-sm">
              {isApproved ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-[#1d3a14]" />
                  <span className="font-semibold text-[#1d3a14]">Approved</span>
                  {approvedBy && approvedBy !== "legacy" && (
                    <span className="text-[#5b6b4a]">
                      by {approvedBy}
                      {approvedAt
                        ? ` · ${new Date(approvedAt).toLocaleString("en-IN")}`
                        : ""}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <Clock className="h-4 w-4 text-[#8a4c0d]" />
                  <span className="font-semibold text-[#8a4c0d]">
                    Pending coordinator approval
                  </span>
                </>
              )}
            </div>
            {canApprove && (
              <Button
                onClick={handleApprove}
                disabled={saving}
                data-testid="approve-customer-button"
                className="bg-[#1d3a14] hover:bg-[#264a1a] text-[#faf6ea]"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Approve Customer
              </Button>
            )}
          </div>
        )}

        {isLockedForUser && (
          <div
            className="no-print bg-[#fbf8ec] border border-[#e7dfc5] rounded-md px-3 py-2 text-xs text-[#5b6b4a]"
            data-testid="customer-locked-notice"
          >
            This customer has been approved and is now locked. Contact a
            coordinator to make further changes.
          </div>
        )}

        {isEdit && (
          <div className="hidden print:block print-card border border-[#d6cdb4] rounded-xl p-5">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <h1 className="brand-serif text-2xl font-bold text-[#1d3a14]">
                  Ramajeyam Rice — Customer Record
                </h1>
                <p className="text-xs text-[#5b6b4a]">
                  Customer ID: {id}
                </p>
              </div>
              <p className="text-xs text-[#5b6b4a]">
                Printed {new Date().toLocaleString("en-IN")}
              </p>
            </div>
          </div>
        )}

        {isEdit && (
          <div
            className="hidden print:block print-card border border-[#d6cdb4] rounded-xl p-6 space-y-5 text-sm"
            data-testid="customer-print-summary"
          >
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              <PrintRow label="Company Name" value={form.company_name || "—"} bold />
              <PrintRow label="Mobile" value={form.mobile || "—"} mono />
              <PrintRow label="Email" value={form.email || "—"} />
              <PrintRow label="GSTIN" value={form.gstin || "—"} mono />
              <PrintRow
                label="Address"
                value={form.address || "—"}
                fullSpan
              />
              <PrintRow label="City" value={form.city || "—"} />
              <PrintRow label="State" value={form.state || "—"} />
              <PrintRow label="Customer Segment" value={form.segment || "—"} />
              <PrintRow label="Payment Terms" value={form.payment_terms || "—"} />
              <PrintRow
                label="Assigned Salesperson"
                value={form.assigned_salesperson_email || "Unassigned"}
                fullSpan
              />
              {createdAt && (
                <PrintRow
                  label="Customer Added"
                  value={new Date(createdAt).toLocaleString("en-IN")}
                  fullSpan
                />
              )}
            </div>
            {form.attachments.length > 0 && (
              <div className="pt-3 border-t border-[#efe7cf]">
                <p className="text-xs uppercase tracking-wider text-[#5b6b4a] mb-1">
                  Attachments ({form.attachments.length})
                </p>
                <ul className="list-disc ml-5">
                  {form.attachments.map((a, i) => (
                    <li key={i}>{a.name}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-2 gap-8 pt-10">
              <div>
                <div className="border-t border-[#1d3a14] pt-1 text-xs">
                  Customer / Authorised Signatory
                </div>
              </div>
              <div>
                <div className="border-t border-[#1d3a14] pt-1 text-xs">
                  Ramajeyam Rice Representative
                </div>
              </div>
            </div>
          </div>
        )}

        <section className="grid md:grid-cols-2 gap-6 print:hidden">
          <div className="bg-white/85 border border-[#d6cdb4] rounded-xl p-5 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-[#1d3a14] flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Business Details
            </h2>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                Company Name *
              </Label>
              <Input
                data-testid="company-name-input"
                value={form.company_name}
                onChange={(e) => set("company_name", e.target.value)}
                placeholder="e.g. Sri Lakshmi Traders"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                <Phone className="inline h-3 w-3 mr-1" /> Mobile *
              </Label>
              <Input
                data-testid="mobile-input"
                value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)}
                placeholder="+91 98765 43210"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                <Mail className="inline h-3 w-3 mr-1" /> Email
              </Label>
              <Input
                data-testid="email-input"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="orders@company.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                GSTIN
              </Label>
              <Input
                data-testid="gstin-input"
                value={form.gstin}
                onChange={(e) =>
                  set("gstin", e.target.value.toUpperCase().slice(0, 15))
                }
                placeholder="22AAAAA0000A1Z5"
                className="mt-1 font-mono uppercase"
                maxLength={15}
              />
              <p className="text-[10px] text-[#5b6b4a] mt-1">
                15-character GSTIN (optional)
              </p>
            </div>
          </div>

          <div className="bg-white/85 border border-[#d6cdb4] rounded-xl p-5 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-[#1d3a14] flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Location
            </h2>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                Address
              </Label>
              <Input
                data-testid="address-input"
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                placeholder="No. 12, Bazaar Street"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  City
                </Label>
                <Input
                  data-testid="city-input"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder="Madurai"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                  State
                </Label>
                <Input
                  data-testid="state-input"
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                  placeholder="Tamil Nadu"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <div className="bg-white/85 border border-[#d6cdb4] rounded-xl p-5 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-[#1d3a14] flex items-center gap-2">
              <Tag className="h-4 w-4" /> Commercial
            </h2>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                Customer Segment
              </Label>
              <Select
                value={form.segment || "none"}
                onValueChange={(v) => set("segment", v === "none" ? "" : v)}
              >
                <SelectTrigger
                  data-testid="segment-select"
                  className="mt-1 h-10"
                >
                  <SelectValue placeholder="Select segment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {SEGMENTS.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`segment-opt-${s.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                <CreditCard className="inline h-3 w-3 mr-1" /> Payment Terms
              </Label>
              <Input
                data-testid="payment-terms-input"
                value={form.payment_terms}
                onChange={(e) => set("payment_terms", e.target.value)}
                placeholder="e.g. Net 30, Advance, COD…"
                className="mt-1"
              />
            </div>
          </div>

          <div className="bg-white/85 border border-[#d6cdb4] rounded-xl p-5 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-[#1d3a14] flex items-center gap-2">
              <UserCircle2 className="h-4 w-4" /> Assignment
            </h2>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                Assigned Salesperson
              </Label>
              {canEditAssignedSP ? (
                <Select
                  value={form.assigned_salesperson_email || "none"}
                  onValueChange={(v) =>
                    set("assigned_salesperson_email", v === "none" ? "" : v)
                  }
                >
                  <SelectTrigger
                    data-testid="assigned-sp-select"
                    className="mt-1 h-10"
                  >
                    <SelectValue placeholder="Pick a salesperson" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {salespersons.map((sp) => (
                      <SelectItem
                        key={sp.email}
                        value={sp.email}
                        data-testid={`sp-opt-${sp.email}`}
                      >
                        <span className="font-medium">{sp.name}</span>
                        <span className="ml-2 text-xs text-[#5b6b4a]">
                          {sp.email}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.assigned_salesperson_email || user?.email || ""}
                  readOnly
                  data-testid="assigned-sp-readonly"
                  className="mt-1 bg-[#faf6ea]"
                />
              )}
              {!canEditAssignedSP && (
                <p className="text-[10px] text-[#5b6b4a] mt-1 italic">
                  Customer is auto-assigned to you. Coordinator can re-assign later.
                </p>
              )}
            </div>
            {createdAt && (
              <div className="text-xs text-[#5b6b4a] pt-2 border-t border-[#efe7cf]">
                Added {new Date(createdAt).toLocaleString("en-IN")}
              </div>
            )}
          </div>
        </section>

        <section className="bg-white/80 border border-[#d6cdb4] rounded-xl p-5 shadow-sm print:hidden">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-[#1d3a14]" />
              <h2 className="text-lg font-semibold text-[#1d3a14]">
                Attachments
              </h2>
              <span className="text-xs text-[#5b6b4a]">
                (PAN, GST cert, agreement…)
              </span>
            </div>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-button"
              className="border-[#c4b991] hover:bg-[#efe7cf]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              data-testid="file-input"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt"
            />
          </div>
          {form.attachments.length === 0 ? (
            <p
              className="text-sm text-[#9aa391] italic"
              data-testid="no-files-msg"
            >
              No files attached. (Optional — max 5 MB per file)
            </p>
          ) : (
            <ul className="space-y-2" data-testid="attachments-list">
              {form.attachments.map((f, idx) => (
                <li
                  key={`${f.name}-${idx}`}
                  className="flex items-center justify-between gap-3 bg-[#fbf8ec] border border-[#e7dfc5] rounded-lg px-3 py-2"
                  data-testid={`attachment-item-${idx}`}
                >
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <Paperclip className="h-4 w-4 shrink-0 text-[#264a1a]" />
                    <span className="truncate font-medium text-[#1f2a1a]">
                      {f.name}
                    </span>
                    <span className="text-xs text-[#5b6b4a] shrink-0">
                      ({formatBytes(f.size)})
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {f.data_base64 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          downloadBase64(f.name, f.type, f.data_base64)
                        }
                        className="border-[#c4b991] hover:bg-[#efe7cf]"
                      >
                        Download
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(idx)}
                      data-testid={`remove-attachment-${idx}`}
                      className="text-[#5b6b4a] hover:text-[#1d3a14]"
                      aria-label="Remove file"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-wrap gap-3 justify-end print:hidden">
          {isEdit && canDelete && (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={saving}
              data-testid="delete-customer-button"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Customer
            </Button>
          )}
          {!isLockedForUser && (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              data-testid="save-customer-button"
              className="bg-[#1d3a14] hover:bg-[#264a1a] text-[#faf6ea]"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Save Customer"}
            </Button>
          )}
        </section>
      </main>
    </div>
  );
}
